import { action, observable, ObservableMap, computed, makeObservable } from "mobx";
import { handleStoreState, CoreStore } from "Stores/RESTStore/CoreStore";
import i18n from "Config/i18n";
import { notificationError } from "utils/libHelpers/AntdNotification";
import { RcFile, UploadFile, UploadFileStatus } from "antd/lib/upload/interface";
import { API } from "General/API";
import { API_URL } from "General/APIEndpoints";
import { projectsDataStore } from "Stores";

export type FileAccess = "PIN" | "ACCESS_TYPE";
export type SubmitMode = "TASK_CREATE" | "TASK_EDIT" | "COMMENT_CREATE" | "COMMENT_EDIT";
export type TaskAccessErrorMode = "ASSIGNEE" | "ATTACH" | "COMMENT";

export interface AccessError {
  code: number;
  filesWithoutViewer: unknown[];
  officeViewerOnly: unknown[];
  internalUserNames: string[];
}
export interface TaskUploadFile extends UploadFile {
  id?: string | number;
  deleted?: boolean;
  accessLevel?: API_FILEATTACHMENT_ACCESSLEVEL;
  viewers?: API_FileDataEntity["viewers"];
  fileActions?: API_FileDataEntity["fileActions"];
}

export class TaskAttachmentsStore extends CoreStore {
  isAccessVisible: boolean = false;
  accessException: FileAccess = null;
  accessInternalUsernames: string[] = [];
  mode: SubmitMode = null;
  filesMap: ObservableMap<string, TaskUploadFile> = new Map() as ObservableMap<string, TaskUploadFile>;
  accessErrorMode: TaskAccessErrorMode = null;
  newAttachFileIds: string[] = [];

  constructor() {
    super();

    makeObservable(this, {
      isAccessVisible: observable,
      accessException: observable,
      accessInternalUsernames: observable,
      mode: observable,
      filesMap: observable,
      accessErrorMode: observable,
      newAttachFileIds: observable,
      clearAll: action.bound,
      toggleAccessModal: action.bound,
      getFileIds: action.bound,
      deleteFile: action.bound,
      addExternalFile: action.bound,
      addInitialFiles: action.bound,
      addProjectFile: action.bound,
      externalFilesLoading: computed,
      fileList: computed,
      setNewAttachFilesIds: action.bound,
      checkRights: action.bound,
      clearNewAttachedFiles: action.bound,
    });

    this.filesMap = new Map() as ObservableMap<string, TaskUploadFile>;
  }

  url(_id?: string | number): string {
    return "";
  }

  clearAll() {
    this.clear();
    this.clearNewAttachedFiles();
  }

  clearNewAttachedFiles(clearAllFiles: boolean = false) {
    if (this.newAttachFileIds.length && this.mode !== "TASK_CREATE") {
      this.newAttachFileIds.forEach((id) => {
        this.deleteFile(id);
      });
    }
    this.newAttachFileIds = [];
    if (clearAllFiles) {
      this.clearFiles();
    }
  }

  clearFiles() {
    if (this.filesMap) {
      this.filesMap.clear();
    } else {
      this.filesMap = new Map() as ObservableMap;
    }
  }

  clear() {
    this.isAccessVisible = false;
    this.accessException = null;
    this.accessErrorMode = null;
    this.mode = null;
    this.clearFiles();
  }

  toggleAccessModal(exception?: FileAccess, usernames?: string[]) {
    if (exception) {
      this.accessException = exception;
      this.accessInternalUsernames = usernames;
      this.isAccessVisible = true;
    } else {
      this.isAccessVisible = false;
      this.accessException = null;
      this.accessInternalUsernames = [];
      this.newAttachFileIds = [];
    }
  }

  @handleStoreState()
  async checkRights(err, mode: SubmitMode) {
    if (err.response && err.response.status === 409 && err.response.data) {
      const { code, internalUserNames } = err.response.data as AccessError;
      switch (code) {
        case 1051:
          notificationError(i18n.t("Error"), i18n.t(`Task was not created/updated`));
          break;
        case 1055:
          this.mode = mode;
          this.toggleAccessModal("PIN", internalUserNames);
          break;
        case 1056:
          this.mode = mode;
          this.accessErrorMode = "ATTACH";
          this.toggleAccessModal("ACCESS_TYPE", internalUserNames);
          break;
        case 1071:
          this.mode = mode;
          this.accessErrorMode = "ASSIGNEE";
          this.toggleAccessModal("ACCESS_TYPE", internalUserNames);
          break;
        case 1072:
          this.mode = mode;
          this.accessErrorMode = "COMMENT";
          this.toggleAccessModal("ACCESS_TYPE", internalUserNames);
          break;
        default:
          this.mode = null;
          this.toggleAccessModal();
          break;
      }
    } else {
      this.mode = null;
      this.toggleAccessModal();
      notificationError(i18n.t("Error"), JSON.stringify(err));
      throw err;
    }
  }

  getFileIds(): string[] {
    const fileIds = this.filesMap.keys();
    return Array.from(fileIds);
  }

  async deleteFile(uid: string) {
    try {
      if (typeof uid === "number") {
        uid = `@id${uid}`;
      }
      const file = this.filesMap.get(uid);
      if (!file) {
        throw new Error(`No file found for ID: ${uid}`);
      }
      this.filesMap.delete(uid);
      if (file.status === "done") {
        // if file belongs to externally attached - delete from temp database
        await API.deleteData(API_URL.FILE_UPLOAD_V3.deleteTemporaryFile(projectsDataStore.projectId, file.id));
      }
    } catch (err) {
      throw err;
    }
  }

  /**
   * Methods for handling file attachments
   */

  fileUrl(id?: string) {
    if (id) {
      return API_URL.FILE_UPLOAD_V3.deleteTemporaryFile(projectsDataStore.projectId, id);
    } else {
      return API_URL.FILE_UPLOAD_V3.uploadTemporaryFile(projectsDataStore.projectId);
    }
  }

  /**
   * Handles adding of files through drag-n-drop component of antd library.
   * "@temp-" suffix used to mark externally loaded files on attaching to email in order to recognize them at back-end.
   * @param file - object that is returned by antd/Upload.Dragger component.
   */
  addExternalFile(file: TaskUploadFile) {
    if (!file.id && file.status === "done") {
      // if file has just finished upload
      const response: API_TemporaryFile[] = file.response;
      if (Array.isArray(response)) {
        if (response.length === 1) {
          // if received only one file
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const uid = `@temp-${response[0].id}` as any; // implicitly use backend file id as a uid value
          this.filesMap.delete(`${file.uid}`);
          file.uid = uid;
          file.id = response[0].id;
          file.name = response[0].name;
          // file.expiration = response[0].expiration; FEATURE: you can add "expiration" prop here
          this.filesMap.set(`${file.uid}`, file);
        } else {
          // back-end extracts zip archive, so we add them as files one by one
          response.forEach((responseFile) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const uid = `@temp-${responseFile.id}` as any; // implicitly use backend file id as a uid value
            const splitFileName = responseFile.name.split(".");

            this.filesMap.set(uid, {
              uid: uid,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              originFileObj: {} as any,
              status: "done" as UploadFileStatus,
              id: responseFile.id,
              size: responseFile.sizeBytes,
              name: responseFile.name,
              type: splitFileName[splitFileName.length - 1], // TODO: add mimeType in back-end response
            });

            this.filesMap.delete(`${file.uid}`); // remove archive from attaches list
          });
        }
      }
    } else {
      // if file is uploading or has already processed
      this.filesMap.set(`${file.uid}`, file);
    }
  }

  addInitialFiles(data: API_FileAttachment[]) {
    if (data?.length) {
      data.forEach((file) => {
        this.addProjectFile(file, false);
      });
    }
  }

  addProjectFile({ id, size, name, type, viewers, fileActions, deleted }: API_FileDataEntity, newFile: boolean = true) {
    const uid = `@id${id}`;
    this.filesMap.set(uid, {
      uid,
      originFileObj: {} as RcFile,
      id,
      size,
      name,
      type,
      viewers,
      fileActions,
      deleted,
    });
    if (newFile) {
      this.setNewAttachFilesIds(uid);
    }
  }

  get externalFilesLoading() {
    return this.fileList.some((file) => file.status && file.status !== "done");
  }

  get fileList(): TaskUploadFile[] {
    return Array.from(this.filesMap.values());
  }

  setNewAttachFilesIds(id: string) {
    const arr = [...this.newAttachFileIds, id];
    const uniqIds = Array.from(new Set(arr));
    this.newAttachFileIds = uniqIds;
    console.log("this.newAttachFileIds", this.newAttachFileIds);
  }
}

export const taskAttachmentsStore = new TaskAttachmentsStore();
